import React from "react";
import { IntlShape } from "react-intl";

import {
  type ProjectSetupOption,
  ProjectSetupType,
  type SelectOption,
} from "../types";
import FormSection from "./FormSection";
import IDHFormattedMessage from "src/app/components/IDHFormattedMessage/IDHFormattedMessage";
import CustomSelect from "src/app/components/CustomSelect/CustomSelect";
import { translateMessage } from "src/app/methods/translateMessage";
import { CustomInputLabel } from "src/app/components/CustomInput/CustomInput";
import ProjectsSearchSelector from "src/app/dropdowns/ProjectsSearchSelector/ProjectsSearchSelector";

interface StructureSectionProps {
  currentSetup: ProjectSetupType;
  onChangeProjectSetup: (newOption: ProjectSetupType) => void;
  intl: IntlShape;
  projectSetupOptions: ProjectSetupOption[];
  selectedBaseTemplateOption: SelectOption | null;
  allBaseOptions: SelectOption[];
  onChangeBaseTemplate: (newOption: SelectOption) => void;
  selectedExistingProjectOption: SelectOption | null;
  onChangeExistingProject: (newOption: SelectOption) => void;
  selectedSocialPlatformTemplateOptions: SelectOption[];
  allSocialPlatformTemplateOptions: SelectOption[];
  onChangeSocialPlatformTemplate: (newOption: SelectOption[]) => void;
}

export default function StructureSection({
  currentSetup,
  onChangeProjectSetup,
  intl,
  projectSetupOptions,
  selectedBaseTemplateOption,
  allBaseOptions,
  onChangeBaseTemplate,
  selectedExistingProjectOption,
  onChangeExistingProject,
  selectedSocialPlatformTemplateOptions,
  allSocialPlatformTemplateOptions,
  onChangeSocialPlatformTemplate,
}: StructureSectionProps) {
  const isBlank = currentSetup === ProjectSetupType.Blank;
  const isTemplate = currentSetup === ProjectSetupType.Template;
  const isExisting = currentSetup === ProjectSetupType.BasedOnExistingProject;

  return (
    <>
      <FormSection
        title={
          <IDHFormattedMessage
            id="ws_campaign_structure"
            defaultMessage="Campaign structure"
          />
        }
        dataQa="project-setup-select"
      >
        <CustomInputLabel required>
          <IDHFormattedMessage id="ws_setup" defaultMessage="Setup" />
        </CustomInputLabel>
        <CustomSelect
          id="setup"
          value={
            projectSetupOptions.find(
              (option) => option.value === currentSetup,
            ) ?? null
          }
          onChange={(newOption: ProjectSetupOption) =>
            onChangeProjectSetup(newOption.value)
          }
          options={projectSetupOptions}
        />
      </FormSection>
      {!isBlank && (
        <>
          <FormSection dataQa="project-template-select">
            <CustomInputLabel required>
              <IDHFormattedMessage id="ws_template" defaultMessage="Template" />
            </CustomInputLabel>
            {isTemplate && (
              <CustomSelect
                id="template"
                closeMenuOnSelect
                value={selectedBaseTemplateOption}
                options={allBaseOptions}
                placeholder={translateMessage({
                  intl,
                  id: "ws_select_template",
                  defaultMessage: "Select template",
                })}
                onChange={onChangeBaseTemplate}
              />
            )}
            {isExisting && (
              <ProjectsSearchSelector
                id="project-template"
                value={selectedExistingProjectOption}
                placeholder={translateMessage({
                  intl,
                  id: "ws_select_campaign",
                  defaultMessage: "Select campaign",
                })}
                handleChange={onChangeExistingProject}
              />
            )}
          </FormSection>
          {isTemplate && (
            <FormSection dataQa="project-social-platform-select">
              <CustomInputLabel>
                <IDHFormattedMessage
                  id="ws_select_social_platforms"
                  defaultMessage="Select Social Platforms"
                />
              </CustomInputLabel>
              <CustomSelect
                id="select-social-platforms"
                isMulti
                commaSeparatedValues
                closeMenuOnSelect={false}
                value={selectedSocialPlatformTemplateOptions}
                options={allSocialPlatformTemplateOptions}
                placeholder={translateMessage({
                  intl,
                  id: "ws_select_platforms",
                  defaultMessage: "Select platforms",
                })}
                onChange={onChangeSocialPlatformTemplate}
              />
            </FormSection>
          )}
        </>
      )}
    </>
  );
}
